import { DateTime } from "luxon";
import { flow } from "lodash";
import {
  mergeUniq,
  sameTimes,
  sameMonths,
  sameWeekDays,
  sameMonthDays,
  ascending,
} from "../helpers";

/**
 * @typedef TimedGeofenceRule
 * @prop {string} startTime
 * @prop {string} endTime
 * @prop {Array<string>} week_days
 * @prop {Array<string>} month_days
 * @prop {Array<string>} months
 * @prop {number} durationInDays
 */

/**
 * @typedef Schedule
 * @prop {Array<TimedGeofenceRule>} rules
 */

/**
 * @param {string} time
 *
 * @returns {number}
 */
const timeToFloat = (time) => parseFloat(time.replace(":", "."));

/**
 * @param {string} timeA
 * @param {string} timeB
 *
 * @returns {string} the earlier time
 */
export const earlierTime = (timeA, timeB) =>
  timeToFloat(timeA) < timeToFloat(timeB) ? timeA : timeB;

/**
 * @param {string} timeA
 * @param {string} timeB
 *
 * @returns {string} the earlier time
 */
export const laterTime = (timeA, timeB) =>
  timeToFloat(timeA) > timeToFloat(timeB) ? timeA : timeB;

/**
 * @param {TimedGeofenceRule} ruleA
 * @param {TimedGeofenceRule} ruleB
 *
 * @returns {boolean}
 */
const sameDays = (ruleA, ruleB) => sameWeekDays(ruleA, ruleB) && sameMonthDays(ruleA, ruleB);

/**
 * @param {TimedGeofenceRule} r1
 * @param {TimedGeofenceRule} r2
 *
 * @returns {boolean}
 */
const ruleTimesMatch = (r1, r2) => sameTimes(r1, r2) && (sameDays(r1, r2) || sameMonths(r1, r2));

/**
 * @param {string} time
 * @returns {luxon.DateTime} the earlier time
 */
const makeDate = (time) => {
  const [hour, minute] = time.split(":");
  return DateTime.fromObject({ hour, minute });
};

/**
 * @param {TimedGeofenceRule} rule
 * @returns {boolean}
 */
const isValidTimeSpan = (rule) => {
  try {
    const start = makeDate(rule.startTime);
    const end = makeDate(rule.endTime);
    const diff = start.diff(end, "minutes");
    return diff.values.minutes !== 0;
  } catch (e) {
    console.error(e);
    return false;
  }
};

/**
 * @param {TimedGeofenceRule} r1
 * @param {TimedGeofenceRule} r2
 *
 * @returns {boolean}
 */
const areTimesContiguous = (r1, r2) => {
  if (!(sameDays(r1, r2) && sameMonths(r1, r2))) return false;

  const startTime = makeDate(earlierTime(r1.endTime, r2.endTime));
  const endTime = makeDate(laterTime(r1.startTime, r2.startTime));

  return endTime.diff(startTime, "minutes").minutes === 1;
};

/**
 *
 * @param {TimedGeofenceRule} r1
 * @param {TimedGeofenceRule} r2
 * @returns {TimedGeofenceRule}
 */
function mergeRules(r1, r2) {
  return {
    ...r1,
    startTime: earlierTime(r1.startTime, r2.startTime),
    endTime: laterTime(r1.endTime, r2.endTime),
    week_days: mergeUniq(r1.week_days, r2.week_days),
    month_days: mergeUniq(r1.month_days, r2.month_days).sort(ascending),
    months: mergeUniq(r1.months, r2.months),
  };
}

export class Compactor {
  constructor(props = {}) {
    const { isCompactableFn, mergeRulesFn } = props;
    this.isCompactable = isCompactableFn;
    this.mergeRules = mergeRulesFn || mergeRules;
  }

  /**
   * @private
   * @param {TimedGeofenceRule} rules
   * @param {TimedGeofenceRule} baseRule
   * @returns {TimedGeofenceRule[]}
   */
  _compactSingleRule(rules, baseRule) {
    for (let i = 0; i < rules.length; i++) {
      if (this.isCompactable(rules[i], baseRule)) {
        rules[i] = this.mergeRules(rules[i], baseRule);
        return rules;
      }
    }
    rules.push(baseRule);
    return rules;
  }

  /**
   * @private
   * @param {TimedGeofenceRule} rules
   * @param {TimedGeofenceRule[]} compactedRules
   * @returns {TimedGeofenceRule[]}
   */
  _compactor(rules) {
    return rules.reduce((output, rule) => this._compactSingleRule(output, rule), []);
  }

  /**
   * @param {Schedule} schedule an object containing the rule groups.
   * @returns {Schedule} an object containing compacted Rules.
   */
  compact(schedule) {
    if (!schedule.rules) return [];

    let rules = schedule.rules.filter(isValidTimeSpan);
    if (rules.length <= 1) return schedule;

    let done = false;
    let compactedRules;

    while (!done) {
      compactedRules = this._compactor(rules);
      if (compactedRules.length === rules.length) {
        done = true;
      }
      rules = [...compactedRules];
    }
    return {
      ...schedule,
      rules: compactedRules,
    };
  }
}

const contiguousTimesCompactor = new Compactor({ isCompactableFn: areTimesContiguous });
const matchingTimesCompactor = new Compactor({ isCompactableFn: ruleTimesMatch });

export default flow(
  (schedule) => contiguousTimesCompactor.compact(schedule),
  (schedule) => matchingTimesCompactor.compact(schedule),
);
